import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from 'react-query';
import { getNavigationItems } from '@/api/navigationService';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [hoverItem, setHoverItem] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);
  const { data: headerLinks = [] } = useQuery('nav-header', () => getNavigationItems('header'));

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > window.innerHeight - 100;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    const handleScrollSpy = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentActiveSection: string | null = null;
      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 100) {
          currentActiveSection = section.getAttribute('id');
        }
      });
      setActiveItem(currentActiveSection);
    };
    window.addEventListener('scroll', handleScrollSpy);
    return () => window.removeEventListener('scroll', handleScrollSpy);
  }, []);

  const navItems = headerLinks.map((link) => ({
    name: link.label.toUpperCase(),
    href: link.url,
    external: link.isExternal,
    shortName: link.label,
  }));

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 100,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else if (item.href.startsWith('/')) {
      navigate(item.href);
    } else {
      scrollToSection(item.href);
      setActiveItem(item.href.substring(1));
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        ref={navRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 w-full z-50 bg-white h-[78px] pb-4 transition-all duration-500"
      >
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between h-[70px] px-4 sm:px-6 lg:px-8 pt-2">
            <div className="flex items-center">
              <motion.a
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="flex items-center cursor-pointer mr-12 relative"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.img
                  src="/vermado_black.png"
                  alt="Logo"
                  className="h-8 w-auto drop-shadow-sm"
                  whileHover={{ filter: "drop-shadow(0 0 8px rgba(177, 160, 109, 0.5))" }}
                />
              </motion.a>

              {/* Desktop Menu */}
              <div className="hidden md:flex items-center">
                {navItems.map((item) => (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item);
                    }}
                    className={`relative text-black text-[13px] tracking-[0.15em] uppercase transition-colors duration-300 px-5 py-[21px] font-['Manrope'] font-medium leading-[18px] overflow-hidden ${
                      activeItem === item.href.substring(1) ? 'text-[#B1A06D]' : 'hover:text-[#B1A06D]'
                    }`}
                    onMouseEnter={() => setHoverItem(item.name)}
                    onMouseLeave={() => setHoverItem(null)}
                  >
                    {item.name}
                    <motion.div
                      className="absolute bottom-[18px] left-5 right-5 h-[1px] bg-[#B1A06D]"
                      initial={{ scaleX: 0, opacity: 0 }}
                      animate={{
                        scaleX: hoverItem === item.name || activeItem === item.href.substring(1) ? 1 : 0,
                        opacity: hoverItem === item.name || activeItem === item.href.substring(1) ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Desktop: Mijn Vermado */}
            <div className="hidden md:flex items-center">
              <motion.button
                onClick={() => navigate('/login')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl font-semibold text-[13px] uppercase tracking-[0.15em] border border-black hover:bg-black/90 transition-colors"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Mijn Vermado
              </motion.button>
            </div>

            {/* Mobile: Hamburger */}
            <div className="md:hidden flex items-center">
              <motion.button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                whileTap={{ scale: 0.9 }}
                className="relative w-10 h-10 flex items-center justify-center"
                aria-label="Menu"
              >
                <div className="w-6 h-5 flex flex-col justify-between">
                  <motion.span
                    className="block h-[2px] bg-black rounded-full origin-left"
                    animate={mobileMenuOpen ? { rotate: 45, y: 0, width: '100%' } : { rotate: 0, y: 0, width: '100%' }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.span
                    className="block h-[2px] bg-black rounded-full"
                    animate={mobileMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.span
                    className="block h-[2px] bg-black rounded-full origin-left"
                    animate={mobileMenuOpen ? { rotate: -45, y: 0, width: '100%' } : { rotate: 0, y: 0, width: '100%' }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Fullscreen Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-white md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col justify-between h-full pt-[90px] pb-12 px-8">
              {/* Nav Links */}
              <div className="flex flex-col gap-1">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <motion.button
                      key={item.name}
                      onClick={() => handleNavClick(item)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="text-left py-4 border-b border-black/5"
                    >
                      <span
                        className={`text-[1.75rem] font-medium tracking-[0.06em] uppercase ${
                          isActive ? 'text-[#B1A06D]' : 'text-black'
                        }`}
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.name}
                      </span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Bottom section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="flex flex-col gap-6"
              >
                <button
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="w-full bg-black text-white text-xs tracking-[0.1em] uppercase py-4 rounded-full font-medium hover:bg-black/85 transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Mijn Vermado
                </button>

                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <a
                      href="tel:+31627225807"
                      className="text-black/50 text-[12px] hover:text-black transition-colors"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      +31 6 27225807
                    </a>
                    <a
                      href="mailto:info@vermado.nl"
                      className="text-black/50 text-[12px] hover:text-black transition-colors"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      info@vermado.nl
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.instagram.com/vermado.nl/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:border-black/30 transition-colors"
                    >
                      <svg className="w-4 h-4 text-black/50" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
